import type { HashTag } from "shared/util/tag";
import { type Entity, ValueError, getSlug } from "shelving";
import { CREATE_NOTE } from "shared/api";
import { AsyncButton, NOTICES, Page, useNavigator } from "shared/react";
import { CHALLENGE_MAX, TRAJECTORY_ENTITY } from "shared/schema";
import { getStringStyle } from "shared/util/style";
import { ChallengeList } from "../challenge/ChallengeList.js";
import { CreatorCondition } from "../create/CreatorCondition.js";
import { GroupList } from "../group/GroupList.js";
import { EmbedMarkup } from "../misc/EmbedMarkup.js";
import { PostGrid } from "../post/PostGrid.js";
import { callAPIWithNotice } from "../util/api.js";
import { useTagNoteItem } from "../util/data.js";

export function HashTagPage({ slug: messySlug }: { slug: string }): React.ReactElement {
	const nav = useNavigator();
	const slug = getSlug(messySlug);
	const name = `#${slug}`;
	const note = useTagNoteItem(messySlug)?.value;
	if (!slug) throw new ValueError("Invalid hash tag", messySlug);
	const hashTag: HashTag = `#${slug}`;
	const entity: Entity = `tag:${slug}`;
	return (
		<Page title={name} path={`/tag/${slug}`}>
			<header className="panel pad-3x" style={getStringStyle("/img/hash.angled.svg", slug)}>
				<div>
					<h1>
						<small className="label">Tag</small>
						<span>{hashTag}</span>
					</h1>
				</div>
				<CreatorCondition creator={TRAJECTORY_ENTITY}>
					<AsyncButton
						mods="pin-top pin-right"
						onClick={async () => {
							const id =
								note?.id ||
								(await callAPIWithNotice(
									CREATE_NOTE,
									{
										creator: TRAJECTORY_ENTITY,
										name,
										content: "",
										privacy: "public",
									},
									NOTICES.load("Creating note..."),
								));
							nav.forward(`/note/${id}/edit`);
						}}
					>
						Edit
					</AsyncButton>
				</CreatorCondition>
			</header>
			{note?.content ? (
				<section className="space-xsmall">
					<EmbedMarkup>{note.content}</EmbedMarkup>
				</section>
			) : null}
			<section className="space-3x">
				<h2>Challenges</h2>
				<ChallengeList
					query={{
						"tags[]": entity,
						"privacy": "public",
						"$order": ["!verified", "!started"],
						"$limit": CHALLENGE_MAX,
					}}
				/>
			</section>
			<section className="space-3x">
				<h2>Posts</h2>
				<PostGrid
					query={{
						"tags[]": entity,
						"privacy": "public",
						"$order": "!date",
						"$limit": 8,
					}}
				/>
			</section>
			<section className="space-3x">
				<h2>Groups</h2>
				<GroupList
					query={{
						"tags[]": entity,
						"open": true,
						"$order": ["!verified", "!members"],
						"$limit": 1,
					}}
				/>
			</section>
		</Page>
	);
}
